/**
 * Stops a signed-in client from requesting their advisor's client list.
 *
 * Several hooks refresh that list after a client record changes, and they decide whether to do so
 * by checking for an advisor. In a client session that check passes, because the session carries
 * the client's own advisor, so the request went out and the server refused it (a 403, served to
 * the browser as 405).
 *
 * This runs ahead of the API middleware and swallows the request action for client sessions. The
 * action therefore reaches neither the server nor the reducer, which would otherwise leave the
 * list marked as refreshing with no response ever arriving to clear it.
 */
import type { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { SessionType } from '@shared';
import { fetchClientsForAdvisorByAdvisorIdRequest } from './clientsModel';
import { createSimpleMiddleware } from './createSimpleMiddleware';
import type { AppState } from './store';

function skipClientsFetchForClientSessionMiddlewareImpl(
  _dispatch: Dispatch<AnyAction>,
  getStore: () => AppState,
  next: Dispatch<AnyAction>,
  action: AnyAction,
) {
  const isAdvisorClientsFetch = action.type === fetchClientsForAdvisorByAdvisorIdRequest.type;
  if (isAdvisorClientsFetch && getStore().session.sessionType === SessionType.client) {
    return;
  }

  next(action);
}

export const skipClientsFetchForClientSessionMiddleware = createSimpleMiddleware(
  skipClientsFetchForClientSessionMiddlewareImpl,
);
